import localforage from 'localforage'

var mediaStore = localforage.createInstance({
  name: 'miral_media_cache',
  storeName: 'blobs'
})

var CACHE_KEY_PREFIX = 'media_'
var LIST_KEY_SUFFIX = '_list'
var COVER_LIST_KEY_SUFFIX = '_cover_list'

function cacheKey(historyId) {
  return CACHE_KEY_PREFIX + String(historyId)
}

function listCacheKey(historyId) {
  return cacheKey(historyId) + LIST_KEY_SUFFIX
}

function coverListCacheKey(historyId) {
  return cacheKey(historyId) + COVER_LIST_KEY_SUFFIX
}

/**
 * 存储媒体 blob
 * @param {string|number} historyId
 * @param {Blob} blob
 * @returns {Promise}
 */
export function saveMediaBlob(historyId, blob) {
  if (!historyId || !blob) return Promise.resolve()
  return mediaStore.removeItem(listCacheKey(historyId)).then(function () {
    return mediaStore.setItem(cacheKey(historyId), blob)
  })
}

/**
 * 同一历史记录下多张图（如 Qwen 多图），与单张 key 互斥
 * @param {string|number} historyId
 * @param {Blob[]} blobs
 * @returns {Promise}
 */
export function saveMediaBlobList(historyId, blobs) {
  if (!historyId || !blobs || !blobs.length) return Promise.resolve()
  return mediaStore.removeItem(cacheKey(historyId)).then(function () {
    return mediaStore.setItem(listCacheKey(historyId), blobs)
  })
}

/**
 * Suno 等多轨封面：与音频列表同级的 IndexedDB 列表（仅封面图 blob）
 */
export function saveMediaCoverBlobList(historyId, blobs) {
  if (!historyId || !blobs || !blobs.length) return Promise.resolve()
  return mediaStore.setItem(coverListCacheKey(historyId), blobs)
}

export function getMediaCoverObjectUrlList(historyId) {
  if (!historyId) return Promise.resolve([])
  return mediaStore.getItem(coverListCacheKey(historyId)).then(function (blobs) {
    if (!blobs || !Array.isArray(blobs) || !blobs.length) return []
    return blobs
      .map(function (b) {
        return b && b.size ? URL.createObjectURL(b) : null
      })
      .filter(Boolean)
  })
}

/**
 * 获取缓存的媒体 blob
 * @param {string|number} historyId
 * @returns {Promise<Blob|null>}
 */
export function getMediaBlob(historyId) {
  if (!historyId) return Promise.resolve(null)
  return mediaStore.getItem(cacheKey(historyId))
}

/**
 * 获取缓存 blob 并生成 Object URL（用于 <img>/<video> src）
 * @param {string|number} historyId
 * @returns {Promise<string|null>}
 */
export function getMediaObjectUrl(historyId) {
  return getMediaBlob(historyId).then(function (blob) {
    if (!blob) return null
    return URL.createObjectURL(blob)
  })
}

/**
 * 删除缓存
 * @param {string|number} historyId
 * @returns {Promise}
 */
export function removeMediaBlob(historyId) {
  if (!historyId) return Promise.resolve()
  return mediaStore.removeItem(cacheKey(historyId)).then(function () {
    return mediaStore.removeItem(listCacheKey(historyId))
  }).then(function () {
    return mediaStore.removeItem(coverListCacheKey(historyId))
  })
}

/**
 * @param {string|number} historyId
 * @returns {Promise<string[]>} Object URL 列表，无缓存时 []
 */
export function getMediaObjectUrlList(historyId) {
  if (!historyId) return Promise.resolve([])
  return mediaStore.getItem(listCacheKey(historyId)).then(function (blobs) {
    if (!blobs || !Array.isArray(blobs) || !blobs.length) return []
    return blobs
      .map(function (b) {
        return b && b.size ? URL.createObjectURL(b) : null
      })
      .filter(Boolean)
  })
}

/**
 * 批量检查哪些 historyId 有缓存，返回 { [historyId]: objectUrl }
 * @param {Array<string|number>} historyIds
 * @returns {Promise<Object>}
 */
export function batchGetMediaObjectUrls(historyIds) {
  if (!historyIds || !historyIds.length) return Promise.resolve({})
  var tasks = historyIds.map(function (id) {
    return getMediaObjectUrl(id).then(function (url) {
      return { id: id, url: url }
    })
  })
  return Promise.all(tasks).then(function (results) {
    var map = {}
    results.forEach(function (r) {
      if (r.url) map[r.id] = r.url
    })
    return map
  })
}

/**
 * 批量解析本地缓存：优先多图列表，否则单 blob
 * @returns {Promise<{ single: Object, multi: Object<string, string[]> }>}
 */
export function batchGetLocalMediaMaps(historyIds) {
  if (!historyIds || !historyIds.length) return Promise.resolve({ single: {}, multi: {} })
  var tasks = historyIds.map(function (id) {
    return getMediaObjectUrlList(id).then(function (list) {
      if (list && list.length) return { id: id, multi: list }
      return getMediaObjectUrl(id).then(function (u) {
        return { id: id, single: u || null }
      })
    })
  })
  return Promise.all(tasks).then(function (results) {
    var single = {}
    var multi = {}
    results.forEach(function (r) {
      if (r.multi) multi[r.id] = r.multi
      else if (r.single) single[r.id] = r.single
    })
    return { single: single, multi: multi }
  })
}
